// src/pages/reports/CustomeChannelReport.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Button, Stack, TextField, FormControl, InputLabel, Select, MenuItem, Tabs, Tab } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { authenticationService } from 'src/services/authentication.service';
import { get } from 'src/services/httpClient.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
dayjs.locale('en-gb');

// Consistent date formatting functions
const formatDisplayDate = (date: dayjs.Dayjs | null) => {
  if (!date) return '';
  return date.format('DD/MM/YYYY');
};

const formatDisplayDateTime = (date: any) => {
  if (!date) return '';
  // Handle string dates that might have milliseconds
  if (typeof date === 'string' && date.includes('.')) {
    date = date.split('.')[0];
  }
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.format('DD/MM/YYYY HH:mm:ss') : '';
};

const formatApiDate = (date: dayjs.Dayjs | null) => {
  if (!date) return '';
  return date.format('YYYY-MM-DD');
};

const CustomeChannelReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [rows, setRows] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [packageChannels, setPackageChannels] = useState([]);
  const [subscriberCounts, setSubscriberCounts] = useState([]);
  const [subscriberSubscriptions, setSubscriberSubscriptions] = useState([]);
  const [subscriptionAgeing, setSubscriptionAgeing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('month'));
  const [statusFilter, setStatusFilter] = useState('all');
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
  
  const statusOptions = [
    { id: 2, label: 'Active', value: 'ACTIVE' },
    { id: 3, label: 'InActive', value: 'INACTIVE' },
  ];

  const columns = [
    { field: 'channelId', headerName: 'Channel ID', flex: 1, minWidth: 120 },
    { field: 'channelName', headerName: 'Channel Name', flex: 1, minWidth: 150 },
    { field: 'types', headerName: 'Channel Type', flex: 1, minWidth: 120 },
    { field: 'drmEnabled', headerName: 'DRM Enabled', flex: 1, minWidth: 100 },
    { field: 'status', headerName: 'Status', flex: 1, minWidth: 100 },
    { field: 'subscribers', headerName: 'Subscribers', flex: 1, minWidth: 100, type: 'number' },
    { 
      field: 'createdDateStr', 
      headerName: 'Channel Created Date', 
      flex: 1, 
      minWidth: 170,
      valueGetter: (params) => {
        return params.row.createdDateStr || formatDisplayDateTime(params.row.createdDate);
      }
    },
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Fetch data for Custom Channel Report (Tab 0)
  useEffect(() => {
    if (!startDate || !endDate || tabIndex !== 0) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const from = formatApiDate(startDate);
        const to = formatApiDate(endDate);
        // Update this API endpoint based on your actual endpoint
        const response = await get(`${process.env.REACT_APP_BASE_API_URL}/api/reports/custom-channels/${vendorId}?fromDate=${from}&toDate=${to}`);
        const dataWithId = response?.data.map((item: any, index: number) => ({
          id: index + 1,
          createdDateStr: formatDisplayDateTime(item.createdDate),
          ...item
        }));
        setRows(dataWithId);
      } catch (error) {
        console.error('Error fetching custom channel report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, tabIndex, vendorId]);

  // Channels per Package (Tab 2)
  useEffect(() => {
    if (tabIndex !== 2) return;

    const fetchPackages = async () => {
      try {
        const data = await get(`${process.env.REACT_APP_BASE_API_URL}/api/reports/packages/channels/${vendorId}`);
         const res = data?.data
        // Format any dates in the response
        const formattedRes = Array.isArray(res) ? res.map((item: any, index: number) => ({
          id: index + 1,
          ...item,
          createdDateStr: item.createdDate ? formatDisplayDateTime(item.createdDate) : undefined,
          updatedDateStr: item.updatedDate ? formatDisplayDateTime(item.updatedDate) : undefined,
        })) : res;
        setPackageChannels(formattedRes);
      } catch (err) {
        console.error('Package channels fetch error', err);
      }
    };

    fetchPackages();
  }, [tabIndex, vendorId]);

  // Subscriber Subscriptions (Tab 4)
  useEffect(() => {
    if (tabIndex !== 4) return;

    const fetchSubscriberData = async () => {
      try {
        const data = await get(`${process.env.REACT_APP_BASE_API_URL}/api/reports/subscriber-subscriptions/${vendorId}`);
        
        const res = data?.data
        // Format any dates in the response
        const formattedRes = Array.isArray(res) ? res.map((item: any, index: number) => ({
          id: index + 1,
          ...item,
          createdDateStr: item.createdDate ? formatDisplayDateTime(item.createdDate) : undefined,
          updatedDateStr: item.updatedDate ? formatDisplayDateTime(item.updatedDate) : undefined,
        })) : res;
        setSubscriberSubscriptions(formattedRes);
      } catch (err) {
        console.error('Subscriber subscriptions fetch error', err);
      }
    };

    fetchSubscriberData();
  }, [tabIndex, vendorId]);

  // Subscription Ageing (Tab 5)
  useEffect(() => {
    if (tabIndex !== 5) return;

    const fetchAgeing = async () => {
      try {
        const data = await get(`${process.env.REACT_APP_BASE_API_URL}/api/reports/subscription-ageing/${vendorId}`);
        
        const res = data?.data
        // Format any dates in the response
        const formattedRes = Array.isArray(res) ? res.map((item: any, index: number) => ({
          id: index + 1,
          ...item,
          startDateStr: item.startDate ? formatDisplayDateTime(item.startDate) : undefined,
          endDateStr: item.endDate ? formatDisplayDateTime(item.endDate) : undefined,
          createdDateStr: item.createdDate ? formatDisplayDateTime(item.createdDate) : undefined,
        })) : res;
        setSubscriptionAgeing(formattedRes);
      } catch (err) {
        console.error('Subscription ageing fetch error', err);
      }
    };

    fetchAgeing();
  }, [tabIndex, vendorId]);

  const filteredRows = rows.filter((row) => {
    const matchesSearch = Object.values(row).some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesStatus = 
      statusFilter === 'all' || 
      (row.status && 
       row.status.toLowerCase() === statusFilter.toLowerCase());
    
    return matchesSearch && matchesStatus;
  });

  // Single download handler for both formats using common service
  const handleDownload = async (format: 'pdf' | 'excel') => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (tabIndex !== 0) {
      alert('Download is only available for Custom Channel Report tab');
      return;
    }

    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const fromStr = formatApiDate(startDate);
      const toStr = formatApiDate(endDate);
      const fromDisplay = formatDisplayDate(startDate);
      const toDisplay = formatDisplayDate(endDate);
      
      // Generate custom filename
      const statusSuffix = statusFilter !== 'all' ? `_${statusFilter}` : '';
      const customFileName = `CustomChannelReport${statusSuffix}_${fromStr}_to_${toStr}_${dayjs().format('HHmmss')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      const success = await reportDownloadService.downloadReport({
        title: 'Custom Channel Report',
        columns,
        rows: filteredRows,
        month: startDate.month() + 1,
        year: startDate.year(),
        fileName: customFileName,
      
        // Excel specific configuration
        excelHeaders: [
          "Channel ID",
          "Channel Name",
          "Channel Type",
          "DRM Enabled",
          "Status",
          "Subscribers",
          "Channel Created Date"
        ],
        excelRowMapper: (row: any) => [
          row.channelId || '',
          row.channelName || '',
          row.types || '',
          row.drmEnabled || '',
          row.status || '',
          row.subscribers || 0,
          row.createdDateStr || formatDisplayDateTime(row.createdDate)
        ]
      }, format);
      
      if (!success) {
        alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error generating ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  // Function to render tab content
  const renderTabContent = () => {
    switch (tabIndex) {
      case 0:
        return (
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            pagination
            getRowHeight={() => 'auto'}
            loading={loading}
            sx={{
              border: 2,
              borderColor: 'rgb(43 44 45)',
              '& .MuiDataGrid-cell': { alignItems: 'start', py: 1.5 },
            }}
          />
        );
      case 1:
        return <pre>{JSON.stringify(monthlyData, null, 2)}</pre>;
      case 2:
        return (
          <DataGrid
            rows={packageChannels}
            columns={[
              { field: 'id', headerName: 'ID', width: 70 },
              { field: 'packageId', headerName: 'Package ID', width: 120 },
              { field: 'packageName', headerName: 'Package Name', width: 200 },
              { field: 'channelId', headerName: 'Channel ID', width: 120 },
              { field: 'channelName', headerName: 'Channel Name', width: 200 },
              { field: 'createdDateStr', headerName: 'Created Date', width: 170 },
              { field: 'updatedDateStr', headerName: 'Updated Date', width: 170 },
            ]}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            pagination
            getRowHeight={() => 'auto'}
            sx={{
              border: 2,
              borderColor: 'rgb(43 44 45)',
              '& .MuiDataGrid-cell': { alignItems: 'start', py: 1.5 },
            }}
          />
        );
      case 3:
        return <pre>{JSON.stringify(subscriberCounts, null, 2)}</pre>;
      case 4:
        return (
          <DataGrid
            rows={subscriberSubscriptions}
            columns={[
              { field: 'id', headerName: 'ID', width: 70 },
              { field: 'subscriberId', headerName: 'Subscriber ID', width: 120 },
              { field: 'subscriberName', headerName: 'Subscriber Name', width: 200 },
              { field: 'packageName', headerName: 'Package Name', width: 200 },
              { field: 'channelName', headerName: 'Channel Name', width: 200 },
              { field: 'subscriptionType', headerName: 'Subscription Type', width: 150 },
              { field: 'createdDateStr', headerName: 'Created Date', width: 170 },
              { field: 'updatedDateStr', headerName: 'Updated Date', width: 170 },
            ]}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            pagination
            getRowHeight={() => 'auto'}
            sx={{
              border: 2,
              borderColor: 'rgb(43 44 45)',
              '& .MuiDataGrid-cell': { alignItems: 'start', py: 1.5 },
            }}
          />
        );
      case 5:
        return (
          <DataGrid
            rows={subscriptionAgeing}
            columns={[
              { field: 'id', headerName: 'ID', width: 70 },
              { field: 'subscriberId', headerName: 'Subscriber ID', width: 120 },
              { field: 'subscriberName', headerName: 'Subscriber Name', width: 200 },
              { field: 'packageName', headerName: 'Package Name', width: 200 },
              { field: 'startDateStr', headerName: 'Start Date', width: 170 },
              { field: 'endDateStr', headerName: 'End Date', width: 170 },
              { field: 'daysRemaining', headerName: 'Days Remaining', width: 130 },
              { field: 'status', headerName: 'Status', width: 120 },
              { field: 'createdDateStr', headerName: 'Created Date', width: 170 },
            ]}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            pagination
            getRowHeight={() => 'auto'}
            sx={{
              border: 2,
              borderColor: 'rgb(43 44 45)',
              '& .MuiDataGrid-cell': { alignItems: 'start', py: 1.5 },
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Custom Channel Report</title>
      </Helmet>

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle
              heading="Custom Channel Report"
              subHeading="Details of content protection and encryption enforcement per channel"
              handleClick={undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasPermission(["OWNER"]) && tabIndex === 0 && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload('excel')}
                  disabled={downloading.excel}
                >
                  {downloading.excel ? 'Generating...' : 'Download Excel'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => handleDownload('pdf')}
                  disabled={downloading.pdf}
                >
                  {downloading.pdf ? 'Generating...' : 'Download PDF'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </PageTitleWrapper>

 

      <Box sx={{ px: 2, py: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
            {tabIndex === 0 && (
              <>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ minWidth: 200 }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem key={status.id} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <DatePicker
              label="Start Date (DD/MM/YYYY)"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY'
                } 
              }}
            />
            <DatePicker
              label="End Date (DD/MM/YYYY)"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY'
                } 
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Box>

      <Box sx={{ height: 'calc(100vh - 320px)', px: 2, pb: 2 }}>
        {renderTabContent()}
      </Box>
    </>
  );
};

export default CustomeChannelReport;